import { createSlice } from "@reduxjs/toolkit";
import axios from "../../axios";
import axiosWithoutInterceptor from "../../axiosWithoutInterceptor";
import getConfig from "../../config";
import { rowsPerPageDefault } from "../../shared/constants";
import { redirectToLoginPage } from "../../shared/sessionUtility";
import { isOneWebDomainRuntime } from "../../shared/Utils";
import transformGrants from "./transformGrants";

const { hostnames, appEnv, apiVersion3 } = getConfig();
const { ecmApiHost, identity, cupHost } = hostnames;
// Configuration
const CONFIG = {
    RETRY: {
        MAX_ATTEMPTS: 3,
        INITIAL_DELAY: 1000,
        MAX_DELAY: 5000,
    },
    REFRESH: {
        BUFFER_TIME: 3 * 60 * 1000, // 3 minutes before expiry
        MIN_REFRESH_INTERVAL: 60 * 1000, // Minimum 1 minute between refreshes
    },
};
export const sessionSlice = createSlice({
    name: "session",
    initialState: {
        isLoading: false,
        isAuthenticated: false,
        provisionStatus: null,
        isInternalUser: false,
        accountId: null,
        userId: null,
        role: null,
        username: null,
        firstName: null,
        lastName: null,
        email: null,
        companyIds: [],
        activeCompany: {
            id: null,
            name: null,
        },
        subscriptions: [],
        environment: null,
        avaTaxAccountId: null,
        avaTaxUserId: null,
        accessToken: null,
        accessTokenExpiresAt: 0,
        isAdminUser: false,
        ecmRole: [],
        rowsPerPage: rowsPerPageDefault,
        transformedGrants: {},
        isIAM: false,
    },
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setData: (state, action) => {
            state.isAuthenticated = action.payload.isAuthenticated;
            state.isInternalUser = action.payload.isInternalUser;
            state.provisionStatus = action.payload.status;
            state.accountId = action.payload.accountId;
            state.userId = action.payload.userId;
            state.role = action.payload.role;
            state.username = action.payload.username;
            state.firstName = action.payload.firstName;
            state.lastName = action.payload.lastName;
            state.email = action.payload.email;
            state.companyIds = action.payload.companyIds;
            state.subscriptions = action.payload.subscriptions;
            state.environment = action.payload.environment;
            state.accessToken = action.payload.accessToken;
            state.accessTokenExpiresAt = action.payload.accessTokenExpiresAt;
            state.isAdminUser = action.payload.role?.includes("Admin");
            state.ecmRole = action.payload.ecmRole;
            state.rowsPerPage = action.payload.rowsPerPage;
            state.scsAvataxCompanyMapping = action.payload.scsAvataxCompanyMapping;

            if (action.payload.activeCompany) {
                state.activeCompany.id = action.payload.activeCompany.id;
                state.activeCompany.name = action.payload.activeCompany.name;
            }

            if (typeof FS !== "undefined") {
                // eslint-disable-next-line no-undef
                FS.identify(action.payload.userId, {
                    displayName: action.payload.username,
                    email: action.payload.email,
                    // id: avalaraSystemUserId,
                    accountId: action.payload.accountId,
                });
            }
            const tgrtPermissions = action.payload?.tgrtPermissions;
            if (tgrtPermissions?.length > 0) {
                // process grants
                state.transformedGrants = transformGrants(
                    tgrtPermissions,
                    action.payload.scsAvataxCompanyMapping?.find(
                        company => company.avaTaxCompanyId === action.payload.activeCompany.id
                    )
                );
                state.isIAM = true;
            }
        },

        setSubscriptions: (state, action) => {
            state.subscriptions = action.payload;
        },
        setRowsPerPage: (state, action) => {
            state.rowsPerPage = action.payload;
        },
    },
});

export const { setLoading, setData, setSubscriptions, setRowsPerPage, setValue } =
    sessionSlice.actions;

export const initializeSession = data => dispatch => {
    dispatch(setData(data));

    // Schedule next refresh based on new expiration time
    if (data?.accessTokenExpiresAt) {
        scheduleNextRefresh(dispatch, data.accessTokenExpiresAt);
    }
};

export const forceRefreshSessionAsync = () => async (dispatch, getState) => {
    const { session } = getState();

    if (session.isLoading) {
        return;
    }
    try {
        const response = await axios.get(`//${ecmApiHost}/api/v3/session`, {
            withCredentials: true,
        });

        if (!response?.data?.isAuthenticated) {
            redirectToLoginPage();
            return;
        }
        dispatch(initializeSession(response?.data));
    } catch {
        dispatch(initializeSession({}));
    } finally {
        dispatch(setLoading(false));
    }
};

// Calculate when to refresh
const calculateRefreshDelay = expiresAt => {
    if (!expiresAt) return 0;

    const currentTime = Date.now();
    const expirationTime = expiresAt * 1000; // Convert to milliseconds
    const timeUntilExpiry = expirationTime - currentTime;
    return Math.max(timeUntilExpiry - CONFIG.REFRESH.BUFFER_TIME, 0);
};

// Schedule next refresh
const scheduleNextRefresh = (dispatch, expiresAt) => {
    if (window.sessionRefreshTimeout) {
        clearTimeout(window.sessionRefreshTimeout);
    }

    if (!expiresAt) return;

    const refreshDelay = calculateRefreshDelay(expiresAt);
    if (refreshDelay > 0) {
        window.sessionRefreshTimeout = setTimeout(() => {
            dispatch(forceRefreshSessionAsync());
        }, refreshDelay);
    } else {
        // Refresh immediately if token is close to expiration
        dispatch(forceRefreshSessionAsync());
    }
};

export const executeWithRetry = async (dispatch, refresh, attempt = 0) => {
    const sessionEndpoint = refresh ? "session/refresh-subscriptions" : "session";

    try {
        dispatch(setLoading(true));
        const response = await axios.get(`//${ecmApiHost}/api/v3/${sessionEndpoint}`, {
            withCredentials: true,
        });
        if (!response?.data?.isAuthenticated) {
            redirectToLoginPage();
            return null;
        }
        dispatch(initializeSession(response?.data));
        return response?.data;
    } catch (error) {
        attempt += 1;
        if (attempt < CONFIG.RETRY.MAX_ATTEMPTS) {
            // Calculate delay with exponential backoff
            const delay = Math.min(
                CONFIG.RETRY.INITIAL_DELAY * 2 ** (attempt - 1),
                CONFIG.RETRY.MAX_DELAY
            );
            await new Promise(resolve => setTimeout(resolve, delay));
            return executeWithRetry(dispatch, refresh, attempt + 1);
        }
        throw error;
    }
};

export const getSessionAsync =
    (refresh = false) =>
    async (dispatch, getState) => {
        const { session } = getState();
        if ((session.isAuthenticated && refresh === false) || session.isLoading) {
            return;
        }
        if (isOneWebDomainRuntime()) {
            try {
                const accessTokenRes = await axiosWithoutInterceptor.get(
                    `${getConfig().hostnames.avaApp}/api/token/exemptions`,
                    {
                        withCredentials: true,
                    }
                );
                if (accessTokenRes?.data?.accessToken) {
                    const token = accessTokenRes.data.accessToken;
                    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Failed to retrieve access token:", error);
            }
        }
        await executeWithRetry(dispatch, refresh);
        dispatch(setLoading(false));
    };

export const updateRowCountPreference = preferenceValue => async dispatch => {
    let preferenceValueUpdated;
    try {
        preferenceValueUpdated = JSON.stringify(preferenceValue);
    } catch {
        preferenceValueUpdated = "{}";
    }
    await axios.post(
        `//${ecmApiHost}/api/${apiVersion3}/ecm-preferences`,
        {
            preference: "rows-per-page",
            preferenceValue: preferenceValueUpdated,
        },
        { withCredentials: true }
    );
    const response = await axios.get(`//${ecmApiHost}/api/v3/session`, {
        withCredentials: true,
    });
    dispatch(setRowsPerPage(response.data.rowsPerPage));
};

export const selectSession = state => state.session;

export const selectTransformedGrants = state => state.session.transformedGrants;

export const selectIsIAM = state => state.session.isIAM;

export const selectEcmRole = state => state.session.ecmRole;

export const selectRole = state => state.session.role;

export const getProduct = state => {
    const { session } = state;
    return session.subscriptions;
};

// Data for the Skylab header WebComponent.
// SEE: https://skylabsdk.avalara.io/layout/header
export const selectSkylabHeader = state => {
    const user = JSON.stringify(
        {
            accountId: state.session.accountId,
            givenName: state.session.firstName,
            familyName: state.session.lastName,
            email: state.session.email,
            isLoggedIn: state.session.isAuthenticated,
            isTechSupport: state.session.isInternalUser,
            emitSwitchAccountsNavEvent: !!state.session.isInternalUser,
        },
        null,
        2
    );

    return {
        user,
        productName: "Customer Exemptions",
        env: appEnv,
        appKey: "certcapture",
        homeUrl: window.location.origin,
        loginUrl: `https://${cupHost}`,
        logoutUrl: `//${ecmApiHost}/api/v3/auth/logout`,
        changePasswordUrl: `https://${identity}/changePassword`,
        switchCompanyUrl: `https://${cupHost}/cup/a/${state.session.accountId}/c/${state.session.activeCompany.id}/companies/select`,
    };
};

export const refreshSessionContextAsync = () => async (dispatch, getState) => {
    const { session } = getState();

    if (session.isLoading) {
        return false;
    }
    try {
        const response = await axiosWithoutInterceptor.post(
            `//${ecmApiHost}/api/v3/session/context/refresh`,
            null,
            {
                withCredentials: true,
            }
        );

        return response?.data?.refreshed ?? false;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Session context refresh failed", error);
        return false;
    }
};

export default sessionSlice.reducer;
